import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-a4bdda08"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "find-properties-container" }
const _hoisted_2 = { class: "grid-container" }

import { computed, ref, inject, watch } from 'vue'
import { AcquaintApiClient } from '@/services/AcquaintApiClient';
import { PropertyFindResult } from '@/types/find/properties/PropertyFindResult';
import { PropertyFindQuery } from '@/types/find/properties/PropertyFindQuery';
import { getErrorMessage } from 'bright-logic-core/src/errors';
import displayErrorMessage from '@/utils/ErrorHandling';
import ModalBase from '@/components/ui/modals/modal-base.vue';
import FindInput from '@/components/find/find-input.vue';
import { DxToolbarItem } from 'devextreme-vue/popup';
import FindPropertyResults from '@/components/find/properties/find-property-results.vue';
import GridFilterSummary from '@/components/ui/grid/grid-filter-summary.vue';
import { GridFilter } from '@/types/ui/GridFilter';
import { FindPropertiesFilters } from '@/types/properties/FindPropertiesFilters';
import FindPropertyFiltersModal from '@/components/find/properties/find-property-filters-modal.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'find-property-modal',
  props: {
    /** 
     * The boolean value used with `v-model` to control the visibility or state of a modal or component.
     * @type {boolean}
     * @required
     */
    modelValue: {
        type: Boolean,
        required: true
    },
    /** 
     * The title displayed for the find property component.
     * @type {String}
     * @default 'Find Property'
     */
    title: {
        type: String,
        default: 'Find Property'
    }
},
  emits: [
    /** 
     * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
     * @property {boolean} value - Indicates whether the modal has been opened or closed.
     */
    'update:modelValue',
    /** 
     * Triggered when a property is selected from the list.
     * @param {Object} selectedProperty - The selected property object.
     * @param {Array} propertyIds - List of all property IDs.
     * @param {Number} index - The index of the selected property.
     */
    'propertySelected'
],
  setup(__props, { emit }) {

const props = __props;





const showModal = computed({
    get: () => props.modelValue,
    set: (value: boolean) => emit('update:modelValue', value)
});

const apiClient = inject('AcquaintApiClient') as AcquaintApiClient
const properties = ref<PropertyFindResult[]>([]);
const searching = ref(false);
const selectedProperty = ref<PropertyFindResult>();
const currentIndex = ref(0);
const showFiltersModal = ref(false);
const searchQuery = ref('');
const searchFilters = ref<FindPropertiesFilters>({
    category: { id: -1, description: '' },
    status: { id: -1, description: '' },
    branch: { id: -1, description: '' }
});

const onInputChanged = async (value: string) => {
    searchQuery.value = value;

    // To avoid unnecessary api calls, only perform search if user has entered more than 2 characters
    if (!value || value.length < 2) {
        selectedProperty.value = undefined;
        properties.value = [];
        return;
    }

    fetchProperties();
}

const fetchProperties = async () => {
    searching.value = true;

    const query: PropertyFindQuery = {
        textToFind: searchQuery.value,
        maxResults: 200,
        filters: {
            branchID: searchFilters.value.branch.id,
            category: searchFilters.value.category.id,
            status: searchFilters.value.status.id
        }
    };

    // Get results back from api
    try {
        properties.value = await apiClient.httpPost<PropertyFindResult[]>('find/property', query);
    } catch (ex) {
        displayErrorMessage(getErrorMessage(ex));
    }

    searching.value = false;
};

/** Handles the Select button click event */
const onSelectClick = () => {
    emitSelectedProperty();
}

/** Handles the row click event of the Find Property Results */
const onRowClick = (e: PropertyFindResult) => {
    selectedProperty.value = e;
    emitSelectedProperty();
};

/** Handles the row selected event of the Find Property Results, the if statement ensure that if e is undefined, it doesnt disbale the select button */
const onRowSelected = (e: PropertyFindResult, rowIndex: number) => {
    if (e) {
        selectedProperty.value = e;
    }

    currentIndex.value = rowIndex;
}

// Handles the user clicking on the ... button on a row, the if statement ensure that if e is undefined, it doesnt disbale the select button
const adaptiveRowSelection = (e: PropertyFindResult) => {
    if (e) {
        selectedProperty.value = e;
    }
}

/** Emits the selected Property and closes the modal */
const emitSelectedProperty = () => {
    showModal.value = false;
    emit('propertySelected', selectedProperty.value, properties.value.map(item => item.id), currentIndex.value);
    properties.value = [];
}

const closeModal = () => {
    showModal.value = false;
    properties.value = [];
};

watch(() => showModal.value, (value) => {
    if (!value) {
        properties.value = [];
    }
});

const summaryFilters = computed(() => {
    const gridFilters: GridFilter[] = [];

    // No filters so return blank array so summary shows All
    if (searchFilters.value.status.id == -1 && searchFilters.value.branch.id == -1 && searchFilters.value.category.id == -1) {
        return [];
    }

    if (searchFilters.value.status.id > -1) {
        gridFilters.push({ title: 'Status', values: [searchFilters.value.status], dataSource: [] });
    }
    if (searchFilters.value.branch.id > -1) {
        gridFilters.push({ title: 'Type', values: [searchFilters.value.branch], dataSource: [] });
    }
    if (searchFilters.value.category.id > -1) {
        gridFilters.push({ title: 'Branch', values: [searchFilters.value.category], dataSource: [] });
    }

    return gridFilters;
});

const filtersApplied = (filtersApplied: FindPropertiesFilters) => {
    searchFilters.value = JSON.parse(JSON.stringify(filtersApplied));

    if (properties.value.length > 0) {
        fetchProperties();
    }
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ModalBase, {
      title: props.title,
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showModal).value = $event)),
      height: "80vh",
      onModalClosed: _cache[2] || (_cache[2] = ($event: any) => (emit('propertySelected', undefined)))
    }, {
      "non-scrollable-content": _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          (showModal.value)
            ? (_openBlock(), _createBlock(FindInput, {
                key: 0,
                onChanged: onInputChanged
              }))
            : _createCommentVNode("", true),
          _createVNode(GridFilterSummary, {
            rowCount: properties.value.length,
            filters: summaryFilters.value,
            onFilterClick: _cache[0] || (_cache[0] = ($event: any) => (showFiltersModal.value = true)),
            dropdownMenuDisable: false,
            showMenu: false
          }, null, 8, ["rowCount", "filters"]),
          _createElementVNode("div", _hoisted_2, [
            _createVNode(FindPropertyResults, {
              data: properties.value,
              searching: searching.value,
              onRowClick: onRowClick,
              onRowSelected: onRowSelected,
              onAdaptiveRowSelection: adaptiveRowSelection
            }, null, 8, ["data", "searching"])
          ])
        ])
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
                text: 'Select',
                disabled: !selectedProperty.value,
                onClick: () => onSelectClick()
            }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: { text: 'Close', onClick: () => closeModal() }
        }, null, 8, ["options"])
      ]),
      _: 1
    }, 8, ["title", "modelValue"]),
    _createVNode(FindPropertyFiltersModal, {
      modelValue: showFiltersModal.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showFiltersModal).value = $event)),
      filters: searchFilters.value,
      onFiltersApplied: filtersApplied
    }, null, 8, ["modelValue", "filters"])
  ], 64))
}
}

})